import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { AuctionCard } from "@/components/auction/AuctionCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { CONDITIONS, SORTS, RATING_OPTIONS } from "@/lib/constants";

const ANY = "all";

export default function Browse() {
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState(null);
    const [categories, setCategories] = useState([]);
    const [specialties, setSpecialties] = useState([]);
    const [q, setQ] = useState(params.get("q") || "");
    const [minP, setMinP] = useState(params.get("min_price") || "");
    const [maxP, setMaxP] = useState(params.get("max_price") || "");
    const [showFilters, setShowFilters] = useState(
        ["specialty", "condition", "min_price", "max_price", "min_rating", "status"].some((k) => params.get(k))
    );

    const category = params.get("category") || ANY;
    const status = params.get("status") || ANY;
    const sort = params.get("sort") || "ending_soon";
    const specialty = params.get("specialty") || ANY;
    const condition = params.get("condition") || ANY;
    const minRating = params.get("min_rating") || ANY;

    useEffect(() => {
        api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
        api.get("/specialties").then(({ data }) => setSpecialties(data?.specialties || [])).catch(() => {});
    }, []);

    // Keep local price/keyword inputs in sync if the URL changes (e.g. arriving from homepage search).
    useEffect(() => { setQ(params.get("q") || ""); setMinP(params.get("min_price") || ""); setMaxP(params.get("max_price") || ""); }, [params]);

    const load = useCallback(async () => {
        setItems(null);
        const qs = { sort };
        if (category !== ANY) qs.category = category;
        if (status !== ANY) qs.status = status;
        if (specialty !== ANY) qs.specialty = specialty;
        if (condition !== ANY) qs.condition = condition;
        if (minRating !== ANY) qs.min_rating = minRating;
        if (params.get("q")) qs.q = params.get("q");
        if (params.get("min_price")) qs.min_price = params.get("min_price");
        if (params.get("max_price")) qs.max_price = params.get("max_price");
        const { data } = await api.get("/auctions", { params: qs });
        setItems(data);
    }, [category, status, sort, specialty, condition, minRating, params]);

    useEffect(() => { load(); }, [load]);

    const update = (key, value) => {
        const next = new URLSearchParams(params);
        if (value !== "" && value !== ANY && value != null) next.set(key, value); else next.delete(key);
        setParams(next);
    };

    const submitSearch = (e) => { e.preventDefault(); update("q", q.trim()); };
    const clearAll = () => { setQ(""); setMinP(""); setMaxP(""); setParams({}); };

    const activeCount = [
        category !== ANY, status !== ANY, specialty !== ANY, condition !== ANY,
        minRating !== ANY, !!params.get("min_price"), !!params.get("max_price"), !!params.get("q"),
    ].filter(Boolean).length;

    return (
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
            <div className="mb-6">
                <h1 className="font-display text-3xl font-semibold tracking-tight">Auctions</h1>
                <p className="text-sm text-muted-foreground">Sealed bids only — one bid per item, per bidder.</p>
            </div>

            <div className="mb-6 rounded-xl border border-border bg-card p-4">
                {/* Primary row */}
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                    <form onSubmit={submitSearch} className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input data-testid="browse-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products, brands, keywords…" className="pl-9" />
                    </form>
                    <div className="flex flex-wrap items-center gap-2">
                        <Select value={category} onValueChange={(v) => update("category", v)}>
                            <SelectTrigger data-testid="filter-category" className="w-[160px]"><SelectValue placeholder="Category" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>All categories</SelectItem>
                                {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={sort} onValueChange={(v) => update("sort", v)}>
                            <SelectTrigger data-testid="filter-sort" className="w-[160px]"><SelectValue placeholder="Sort" /></SelectTrigger>
                            <SelectContent>
                                {SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Button type="button" variant="outline" data-testid="browse-toggle-filters" onClick={() => setShowFilters((s) => !s)} className="rounded-[10px]">
                            <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filters
                            {activeCount > 0 && <span className="ml-1.5 rounded-full bg-brass/20 px-1.5 text-xs font-semibold text-brass">{activeCount}</span>}
                        </Button>
                    </div>
                </div>

                {/* Advanced filters */}
                {showFilters && (
                    <div data-testid="browse-filters" className="mt-4 grid grid-cols-1 gap-4 border-t border-border pt-4 sm:grid-cols-2 lg:grid-cols-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs">Specialty</Label>
                            <Select value={specialty} onValueChange={(v) => update("specialty", v)}>
                                <SelectTrigger data-testid="filter-specialty"><SelectValue placeholder="Any specialty" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ANY}>Any specialty</SelectItem>
                                    {specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Condition</Label>
                            <Select value={condition} onValueChange={(v) => update("condition", v)}>
                                <SelectTrigger data-testid="filter-condition"><SelectValue placeholder="Any condition" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ANY}>Any condition</SelectItem>
                                    {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Seller rating</Label>
                            <Select value={minRating} onValueChange={(v) => update("min_rating", v)}>
                                <SelectTrigger data-testid="filter-rating"><SelectValue placeholder="Any rating" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ANY}>Any rating</SelectItem>
                                    {RATING_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Min price ($)</Label>
                            <Input data-testid="filter-min-price" type="number" min="0" value={minP}
                                onChange={(e) => setMinP(e.target.value)}
                                onBlur={() => update("min_price", minP)}
                                onKeyDown={(e) => e.key === "Enter" && update("min_price", minP)}
                                placeholder="0" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Max price ($)</Label>
                            <Input data-testid="filter-max-price" type="number" min="0" value={maxP}
                                onChange={(e) => setMaxP(e.target.value)}
                                onBlur={() => update("max_price", maxP)}
                                onKeyDown={(e) => e.key === "Enter" && update("max_price", maxP)}
                                placeholder="Any" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Status</Label>
                            <Select value={status} onValueChange={(v) => update("status", v)}>
                                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Any status" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ANY}>All statuses</SelectItem>
                                    <SelectItem value="OPEN">Open</SelectItem>
                                    <SelectItem value="CLOSED_TIME">Closed (time)</SelectItem>
                                    <SelectItem value="CLOSED_MAX_BIDDERS">Closed (full)</SelectItem>
                                    <SelectItem value="PAID">Paid</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}

                {activeCount > 0 && (
                    <div className="mt-3 flex justify-end">
                        <button type="button" data-testid="browse-clear-filters" onClick={clearAll} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                            <X className="h-3.5 w-3.5" /> Clear all filters
                        </button>
                    </div>
                )}
            </div>

            {items !== null && (
                <p className="mb-4 text-sm text-muted-foreground" data-testid="browse-result-count">
                    {items.length} {items.length === 1 ? "result" : "results"}
                </p>
            )}

            {items === null ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"].map((k) => <Skeleton key={k} className="h-96 w-full rounded-xl" />)}
                </div>
            ) : items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-16 text-center">
                    <p className="font-display text-lg font-semibold">No auctions found</p>
                    <p className="mt-1 text-sm text-muted-foreground">Try widening your filters or clearing some of them.</p>
                    <Button className="mt-4 rounded-[10px]" variant="secondary" onClick={clearAll}>Clear filters</Button>
                </div>
            ) : (
                <div data-testid="auctions-grid" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {items.map((a) => <AuctionCard key={a.id} a={a} />)}
                </div>
            )}
        </div>
    );
}
